import React, { use, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { LandingStyles } from '../styles/LandingStyles';
import { useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../hooks/useAuthenticated';

const Landing = () => {
  const navigate = useNavigate();

  const { isAuthenticated, isPlanActive } = useAuthenticated();

  useEffect(() => {
    if (isAuthenticated && isPlanActive) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isPlanActive, navigate]);

  return (
    <>
      <Box sx={LandingStyles.root}>
        <Box sx={LandingStyles.leftSection}>
          <Box
            component="img"
            src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986376/landing_page_table_lfkai4.png"
            alt="Laptop, Watch, and Phone on Table"
            sx={LandingStyles.image}
          />
        </Box>
        <Box sx={LandingStyles.rightSection}>
          <Typography sx={LandingStyles.welcome}>WELCOME TO</Typography>
          <Box sx={LandingStyles.logoTitleRow}>
            <Box
              component="img"
              src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986194/yin_yang_mpsxtn.png"
              alt="Yin Yang Logo"
              sx={LandingStyles.logo}
            />
            <Typography sx={LandingStyles.title}>Prep Qi</Typography>
          </Box>
          <Typography sx={LandingStyles.subtitle}>TCM TEST BANK</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: '1rem',
              alignItems: 'center',
            }}
          >
            <Button
              sx={LandingStyles.button}
              onClick={() => navigate('/login')}
            >
              LOGIN
            </Button>
            <Button
              component="a"
              href="#practice-section"
              sx={LandingStyles.button}
            >
              LEARN MORE
            </Button>
          </Box>
        </Box>
      </Box>
      {/* Section Two - Practice Section */}
      <Box id="practice-section" sx={LandingStyles.sectionTwo}>
        <Box sx={LandingStyles.sectionTwoLeft}>
          <Typography sx={LandingStyles.sectionTwoTitle}>
            Feel Prepared Because You've Actually Practiced.
          </Typography>
          <Typography sx={LandingStyles.sectionTwoBold}>
            You've got the notes.
          </Typography>
          <Typography sx={LandingStyles.sectionTwoText}>
            We help you apply them with focused case-based quizzes.
          </Typography>
          <Button
            sx={LandingStyles.sectionTwoButton}
            onClick={() => navigate('/login')}
          >
            Get 2 days Full Access for $1
          </Button>
          <Typography sx={LandingStyles.sectionTwoSubtext}></Typography>
        </Box>
        <Box sx={LandingStyles.sectionTwoRight}>
          <Box
            component="img"
            src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986376/laptop_styjq7.png"
            alt="Quiz on Laptop"
            sx={LandingStyles.sectionTwoLaptop}
          />
        </Box>
      </Box>
      <Box sx={LandingStyles.sectionThree}>
        <Typography sx={LandingStyles.sectionThreeTitle}>
          Overwhelmed by Bootcamps, PDFs, and Notes?
        </Typography>
        <Box sx={{ ...LandingStyles.painPointsRow }}>
          <Box sx={LandingStyles.painPoint}>
            {/* User Icon */}
            <Box sx={LandingStyles.painPointIcon}>
              <svg width="150" height="150" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="9"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M4 20c0-3.3137 3.134-6 7-6s7 2.6863 7 6"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </Box>
            <Typography sx={LandingStyles.painPointText}>
              All content and no feedback? No wonder the work feels endless.
            </Typography>
          </Box>
          <Box sx={LandingStyles.painPoint}>
            {/* Spinner Icon */}
            <Box sx={LandingStyles.painPointIcon}>
              <svg width="150" height="150" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.2"
                />
                <path
                  d="M12 3a9 9 0 0 1 9 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </Box>
            <Typography sx={LandingStyles.painPointText}>
              You study hard—then forget it all. Why? No practice.
            </Typography>
          </Box>
          <Box sx={LandingStyles.painPoint}>
            {/* Checkmark Icon */}
            <Box sx={LandingStyles.painPointIcon}>
              <svg width="150" height="150" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 13l3 3 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Typography sx={LandingStyles.painPointText}>
              You've spent hundreds on prep and still feel unprepared.
            </Typography>
          </Box>
        </Box>
        <Typography sx={LandingStyles.sectionThreeQuote}>
          "TCM School doesn't teach us how to think for board exams. But this
          does"
        </Typography>
      </Box>
      {/* Section Four - What Makes Prep Qi Different */}
      <Box sx={LandingStyles.sectionFour}>
        <Box sx={LandingStyles.sectionFourBubble}></Box>
        <Box sx={LandingStyles.sectionFourRight}>
          <Typography sx={LandingStyles.sectionFourTitle}>
            What Make Prep Qi
            <br />
            Different?
          </Typography>
          <Box component="ul" sx={LandingStyles.featureList}>
            <Box component="li" sx={LandingStyles.featureItem}>
              <Box sx={LandingStyles.checkIcon}>✔️</Box>
              Focus on Diagnosis & Internal Medicine
            </Box>
            <Box component="li" sx={LandingStyles.featureItem}>
              <Box sx={LandingStyles.checkIcon}>✔️</Box>
              Sourced from Textbooks used for Board Exams
            </Box>
            <Box component="li" sx={LandingStyles.featureItem}>
              <Box sx={LandingStyles.checkIcon}>✔️</Box>
              Clear Explanations for Correct & Incorrect Answers
            </Box>
            <Box component="li" sx={LandingStyles.featureItem}>
              <Box sx={LandingStyles.checkIcon}>✔️</Box>
              Case Study Style Questions for Every TCM Pattern
            </Box>
            <Box component="li" sx={LandingStyles.featureItem}>
              <Box sx={LandingStyles.checkIcon}>✔️</Box>
              Over 500 + MC Style Questions
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Section Five - Letter from the Founder */}
      <Box sx={LandingStyles.founderLetterSection}>
        <Box sx={LandingStyles.founderLetterContainer}>
          <Box sx={LandingStyles.founderLetterHeader}>
            {/* Envelope with heart SVG */}
            <Box sx={LandingStyles.founderLetterEnvelope}>
              <svg
                width="60"
                height="48"
                viewBox="0 0 60 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="2"
                  y="8"
                  width="56"
                  height="36"
                  rx="6"
                  fill="#e3f8ff"
                  stroke="#b3c6e7"
                  strokeWidth="2"
                />
                <path d="M2 8L30 32L58 8" stroke="#b3c6e7" strokeWidth="2" />
                <path d="M30 32L2 44V8" stroke="#b3c6e7" strokeWidth="2" />
                <path d="M30 32L58 44V8" stroke="#b3c6e7" strokeWidth="2" />
                <path
                  d="M30 22c-2.5-2-7-6.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 37 12c0 3.5-4.5 8-7 10z"
                  fill="#ffb6c1"
                  stroke="#f06292"
                  strokeWidth="1.5"
                />
              </svg>
            </Box>
            <Typography sx={LandingStyles.founderLetterTitle}>
              A Letter from the Founder
            </Typography>
          </Box>
          <Box sx={LandingStyles.founderLetterBody}>
            <Typography sx={LandingStyles.founderLetterText}>
              Hey, I'm so glad you found this.
              <br />
              <br />
              When I was studying for boards, I felt overwhelmed by all the
              content—and frustrated. I didn't need more lectures or flashcards.
              I needed to apply what I knew and get familiar with the format I'd
              actually be tested in.
              <br />
              <br />
              The truth is, most schools don't prepare us for board-style
              multiple choice. It's not their fault—they're on a tight
              timeline—but it leaves us having to figure out the exam approach
              on our own. That's where Prep Qi came in.
              <br />
              <br />
              I built this because I wanted a massive test bank that helped me
              practice diagnosing, choosing the right points, and spotting
              patterns—while also reviewing as I went. It was the only thing
              that made me feel confident walking into the exam.
              <br />
              <br />
              Prep Qi is for students who know the basics but want to connect
              the dots, sharpen their clinical thinking, and feel prepared for
              the real thing.
              <br />
              <br />
              If that's you, I hope this helps you feel steady, calm, and more
              than ready for what's ahead.
              <br />
              <br />
              You've got this.
              <br />
              <br />
              <br />
              Your Founder:
              <br />
              <br />
              Vita
              <br />
              Acu, Class 2024
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Testimonial Section - Why Students Love It */}
      <Box sx={LandingStyles.testimonialSection}>
        <Typography sx={LandingStyles.testimonialTitle}>
          Why Students Love It
        </Typography>
        <Box sx={LandingStyles.testimonialCards}>
          <Box sx={LandingStyles.testimonialCard}>
            <Box sx={LandingStyles.testimonialStarsAndTextContainer}>
              <Box sx={LandingStyles.testimonialStars}>★★★★★</Box>
              <Typography sx={LandingStyles.testimonialText}>
                I couldn't afford a Bootcamp—this gave me exactly what I needed
                without the stress. All I had to do was review my Bio Med,
                Safety + Points and I was set for Boards!
              </Typography>
            </Box>
            <Box sx={LandingStyles.testimonialNameContainer}>
              <Typography sx={LandingStyles.testimonialName}>Angel</Typography>
              <Typography sx={LandingStyles.testimonialClass}>
                Acu, Class of 2026
              </Typography>
            </Box>
          </Box>
          <Box sx={LandingStyles.testimonialCard}>
            <Box sx={LandingStyles.testimonialStarsAndTextContainer}>
              <Box sx={LandingStyles.testimonialStars}>★★★★★</Box>
              <Typography sx={LandingStyles.testimonialText}>
                I walked out of the case studies feeling confident! The language
                didn't trip me up, and my point selections mirrored what I
                practiced here. I'm so grateful I took the time to go through
                this—it paid off!
              </Typography>
            </Box>
            <Box sx={LandingStyles.testimonialNameContainer}>
              <Typography sx={LandingStyles.testimonialName}>Sarah</Typography>
              <Typography sx={LandingStyles.testimonialClass}>
                Acu, Class of 2024
              </Typography>
            </Box>
          </Box>
          <Box sx={LandingStyles.testimonialCard}>
            <Box sx={LandingStyles.testimonialStarsAndTextContainer}>
              <Box sx={LandingStyles.testimonialStars}>★★★★★</Box>
              <Typography sx={LandingStyles.testimonialText}>
                I bought a Bootcamp... but used this way more. I finally
                understood how to break down cases. The explanations helped me
                alot.
              </Typography>
            </Box>
            <Box sx={LandingStyles.testimonialNameContainer}>
              <Typography sx={LandingStyles.testimonialName}>Denis</Typography>
              <Typography sx={LandingStyles.testimonialClass}>
                Acu, Class of 2027
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={LandingStyles.finalSection}>
        <Typography sx={LandingStyles.finalHeadline}>
          Your future starts now – unlock Prep Qi for just $1
        </Typography>
        <Button
          sx={LandingStyles.finalButton}
          onClick={() => navigate('/login')}
        >
          Start for $1 today
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={LandingStyles.footer}>
        <Typography sx={LandingStyles.footerText}>
          © 2025 Prep Qi. All rights reserved.
        </Typography>
      </Box>
    </>
  );
};

export default Landing;
